import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { AccessRightType, ApplicationSettingsType } from '@fi-shared/types';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { fiHasAccessToApplicationType } from '@fi-shared/utils';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { IPostReadsData } from '@fi-shared/fi-news-feed-shared/fi-news-feed-buttons/fi-reads-count-button/fi-reads-data';
import {
    FiReadsUserType,
    FiUserInteractionsDialogComponent,
} from '@fi-shared/fi-news-feed-shared/fi-news-feed-buttons/fi-reads-count-button/fi-reads-user-list-data-dialog/fi-reads-user-list-data-dialog.component';

@Component({
    selector: 'fi-reads-count-button',
    styleUrls: ['./fi-reads-count-button.component.scss'],
    template: `
        @if (showReadsCount) {
          <div class="fi-reads-count-button-container">
            <button
              mat-icon-button
              (mouseenter)="toggleInfoShownStatus(true)"
              (mouseleave)="toggleInfoShownStatus(false)"
              (click)="openDetailsDialog($event)"
              >
              <mat-icon>
                <span class="fas fa-eye fa-lg"></span>
              </mat-icon>
              <span [hidden]="!isReadsDataPreset()" class="fi-info-icon static-position">
                @if (isInfoShown && isReadsDataPreset() && !isMobilePhone()) {
                  <mat-card class="info-popup">
                    @for (readData of readsData | slice: 0 : readsToShowCount; track readData) {
                      <div
                        class="info-popup_user-name"
                        >
                        {{ readData.UserProfile.FullName }}
                      </div>
                    }
                    @if (getReadsRemainText()) {
                      <div class="bold info-popup_user-name">
                        {{ getReadsRemainText() }}
                      </div>
                    }
                  </mat-card>
                }
              </span>
            </button>
            @if (isReadsDataPreset()) {
              <span class="fi-reads-count-button-value">{{ readsData.length }}</span>
            }
          </div>
        }
        `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-reads-count-button' },
    standalone: false,
})
export class FiReadsCountButtonComponent implements OnInit {
    @Input() objectId: ObjectTypes;
    @Input() objectType: ObjectTypes;
    @Input() createdByUserId: number;
    @Input() readsData: IPostReadsData[] = [];
    showReadsCount: boolean;
    isInfoShown = false;
    readsToShowCount = 10;

    constructor(
        private appTextService: AppTextService,
        private userDataService: FiUserDataService,
        private fiDialogsService: FiDialogsService,
        private detectDevice: DetectDevicesService,
    ) {}

    ngOnInit() {
        const currentUser = this.userDataService.userData;
        this.showReadsCount = (() => {
            if (this.objectType === ObjectTypes.Post) {
                return (
                    currentUser.UserId === this.createdByUserId ||
                    fiHasAccessToApplicationType(
                        ApplicationSettingsType.PostLocal,
                        AccessRightType.Admin,
                        currentUser.ModuleAccessRights,
                    )
                );
            }
            if (this.objectType === ObjectTypes.ForumPost) {
                return (
                    currentUser.UserId === this.createdByUserId ||
                    fiHasAccessToApplicationType(
                        ApplicationSettingsType.ForumAdmin,
                        AccessRightType.Read,
                        currentUser.ModuleAccessRights,
                    )
                );
            }
            return false;
        })();
    }

    toggleInfoShownStatus(value: boolean) {
        this.isInfoShown = value;
    }

    openDetailsDialog($event) {
        $event.stopPropagation();
        if (this.readsData.length) {
            this.fiDialogsService.custom({
                hideFooter: true,
                hideHeader: true,
                contentComponentRef: FiUserInteractionsDialogComponent,
                contentComponentData: {
                    objectId: this.objectId,
                    objectType: this.objectType,
                    readsUserType: FiReadsUserType.ShownBy,
                    usersData: this.readsData,
                },
                config: {
                    panelClass: 'fi-reads-user-list-data-panel',
                },
            });
        }
    }

    getReadsRemainText(): string {
        if (this.readsData.length > this.readsToShowCount)
            return String.format(
                this.appTextService.get('and_others'),
                this.readsData.length - this.readsToShowCount,
            ).toLowerCase();
        return null;
    }

    isMobilePhone(): boolean {
        return this.detectDevice.isMobilePhone();
    }

    isReadsDataPreset(): boolean {
        return this.readsData && this.readsData.length > 0;
    }
}
